import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../api";
import type { CursorPage, Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS, POLL_WATCH_MS } from "./intervals";

export type Batch = Datified<Schemas["Batch"], "created_at">;
export type BatchCounts = Schemas["BatchCounts"];

export interface BatchListFilters {
  cursor?: string;
  limit?: number;
}

/**
 * Whether any batch on screen is still delivering — the same test
 * `batchStatus` draws "In progress" from. Batch counts are derived by
 * grouping message states, never stored, so a page of them is the most
 * expensive read in the admin API: it watches at the monitoring cadence
 * rather than the live one, and only on the first page.
 */
export function isBatchListLive(
  filters: Pick<BatchListFilters, "cursor">,
  items: Batch[] | undefined,
): boolean {
  if (filters.cursor) return false;
  return (items ?? []).some((b) => b.counts.pending + b.counts.leased > 0);
}

export function useBatchList(filters: BatchListFilters = {}) {
  const u = new URLSearchParams();
  if (filters.cursor) u.set("cursor", filters.cursor);
  if (filters.limit) u.set("limit", String(filters.limit));
  const s = u.toString();
  return useQuery<CursorPage<Batch>>({
    queryKey: ["batches", filters],
    queryFn: () => apiGet<CursorPage<Batch>>("/batches" + (s ? "?" + s : "")),
    refetchInterval: (query) =>
      isBatchListLive(filters, query.state.data?.items)
        ? POLL_WATCH_MS
        : POLL_BASE_MS,
  });
}

export function useCancelBatch() {
  const qc = useQueryClient();
  return useMutation<Batch, unknown, { id: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id }) => apiPost<Batch>(`/batches/${id}/cancel`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["batches"] });
      void qc.invalidateQueries({ queryKey: ["messages"] });
    },
  });
}
